class FiltersModelSearch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return

    this.searchBox = this.querySelector('[data-filters-search-box]')
    this.searchInput = this.querySelector('[data-filters-search-input]')
    this.searchStatus = this.querySelector('[data-filters-search-status]')
    this.searchStatusLabel = this.querySelector('[data-filters-search-status-label]')
    this.searchCount = this.querySelector('[data-filters-search-count]')
    this.searchResults = this.querySelector('[data-filters-search-results]')
    this.searchEmpty = this.querySelector('[data-filters-search-empty]')
    this.resultTemplate = this.querySelector('[data-filters-search-result-template]')

    if (
      !this.searchBox ||
      !this.searchInput ||
      !this.searchStatus ||
      !this.searchStatusLabel ||
      !this.searchCount ||
      !this.searchResults ||
      !this.searchEmpty
    ) {
      return
    }

    if (!this.initializeSearch()) return

    this.initialized = true
    this.listenerAbort = new AbortController()
    const { signal } = this.listenerAbort
    this.addEventListener('click', this.onClick, { signal })
    this.addEventListener('keydown', this.onKeydown, { signal })
    this.searchInput.addEventListener('focus', this.onSearchFocus, { signal })
    this.searchInput.addEventListener('input', this.onSearchInput, { signal })
    this.searchInput.addEventListener('keydown', this.onSearchKeydown, { signal })
    this.searchBox.addEventListener('focusout', this.onSearchFocusout, { signal })
    document.addEventListener('pointerdown', this.onSearchPointerDown, { capture: true, signal })

    if (document.activeElement === this.searchInput || this.searchInput.value) {
      this.renderSearchResults(this.searchInput.value)
    }
    if (document.activeElement === this.searchInput) this.classList.add('is-active')
    if (this.searchInput.value) this.scheduleSearchInputEvent()

    this.searchBox.dataset.searchLoading = 'false'
    this.searchBox.setAttribute('aria-busy', 'false')
  }

  disconnectedCallback() {
    this.listenerAbort?.abort()
    if (this.searchRenderFrame) window.cancelAnimationFrame(this.searchRenderFrame)
    if (this.searchInputEventTimer) window.clearTimeout(this.searchInputEventTimer)
  }

  initializeSearch() {
    const configElement = this.querySelector('[data-filters-search-config]')
    if (!configElement) return false

    try {
      this.searchConfig = JSON.parse(configElement.textContent)
    } catch {
      return false
    }

    let { source } = this.searchConfig
    if (typeof source === 'string') {
      try {
        source = JSON.parse(source)
      } catch {
        return false
      }
    }
    if (!Array.isArray(source)) source = []

    this.searchItems = source.flatMap(group =>
      Array.isArray(group.children)
        ? group.children.map(child => ({
            groupName: String(group.name || ''),
            shortName: String(child.shortName || ''),
            normalizedShortName: this.normalizeSearchValue(child.shortName || ''),
            imageUrl: String(child.image_url || ''),
            popular: child.popular === true
          }))
        : []
    )
    this.searchConfig.source = undefined

    return true
  }

  onClick = event => {
    const searchClose = event.target.closest('[data-filters-search-close]')
    if (searchClose && this.contains(searchClose)) {
      this.deactivateSearch()
      return
    }

    const searchResult = event.target.closest('[data-filters-search-result]')
    if (searchResult && this.contains(searchResult)) this.selectSearchResult(searchResult)
  }

  onKeydown = event => {
    const searchResult = event.target.closest('[data-filters-search-result]')
    if (searchResult && this.contains(searchResult)) this.handleSearchResultKeydown(event, searchResult)
  }

  onSearchFocus = () => {
    this.classList.add('is-active')
    this.renderSearchResults(this.searchInput.value)
  }

  onSearchInput = () => {
    this.scheduleSearchInputEvent()

    if (this.searchRenderFrame) window.cancelAnimationFrame(this.searchRenderFrame)
    this.searchRenderFrame = window.requestAnimationFrame(() => {
      this.searchRenderFrame = null
      this.renderSearchResults(this.searchInput.value)
    })
  }

  onSearchKeydown = event => {
    if (event.key === 'Escape') {
      this.deactivateSearch()
      return
    }

    if (event.key !== 'ArrowDown') return

    const firstResult = this.searchResults.querySelector('[data-filters-search-result]')
    if (!firstResult) return

    event.preventDefault()
    firstResult.focus()
  }

  onSearchFocusout = event => {
    if (event.target === this.searchInput) this.flushSearchInputEvent()
  }

  onSearchPointerDown = event => {
    if (!(event.target instanceof Element)) return
    if (this.isFullscreenSearch() || !this.classList.contains('is-active')) return
    if (this.searchBox.contains(event.target)) return

    this.deactivateSearch()
  }

  isFullscreenSearch() {
    return window.matchMedia('(max-width: 1024px)').matches
  }

  deactivateSearch() {
    this.flushSearchInputEvent()
    this.classList.remove('is-active')
    this.searchInput.value = ''
    this.closeSearchResults()
    this.searchInput.blur()
  }

  scheduleSearchInputEvent() {
    if (this.searchInputEventTimer) window.clearTimeout(this.searchInputEventTimer)

    this.searchInputEventDirty = Boolean(this.searchInput.value.trim())
    if (!this.searchInputEventDirty) {
      this.searchInputEventTimer = null
      return
    }

    this.searchInputEventTimer = window.setTimeout(() => {
      this.searchInputEventTimer = null
      this.sendSearchInputEvent()
    }, 3000)
  }

  flushSearchInputEvent() {
    if (this.searchInputEventTimer) {
      window.clearTimeout(this.searchInputEventTimer)
      this.searchInputEventTimer = null
    }
    this.sendSearchInputEvent()
  }

  sendSearchInputEvent() {
    const value = this.searchInput.value.trim()
    if (!this.searchInputEventDirty || !value) return

    this.searchInputEventDirty = false
    if (typeof window.WDSendEvent !== 'function') return

    window.WDSendEvent('wd_search_input', {
      event_source: window.location.pathname,
      value
    })
  }

  normalizeSearchValue(value) {
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
  }

  getSearchResults(query) {
    const normalizedQuery = this.normalizeSearchValue(query)
    const minSearchLength = Number(this.searchConfig.minSearchLength) || 1
    const maxSearchResults = Math.max(1, Number(this.searchConfig.maxSearchResults) || 10)
    if (normalizedQuery.length < minSearchLength) {
      return this.searchItems.filter(item => item.popular).slice(0, maxSearchResults)
    }

    return this.searchItems
      .filter(item => item.normalizedShortName.includes(normalizedQuery))
      .sort((firstItem, secondItem) => {
        const getRank = name => {
          if (name === normalizedQuery) return 0
          if (name.startsWith(normalizedQuery)) return 1
          return 2
        }

        return (
          getRank(firstItem.normalizedShortName) - getRank(secondItem.normalizedShortName) ||
          firstItem.normalizedShortName.localeCompare(secondItem.normalizedShortName)
        )
      })
      .slice(0, maxSearchResults)
  }

  renderSearchResults(query) {
    if (!this.searchItems) return

    const normalizedQuery = this.normalizeSearchValue(query)
    const minSearchLength = Number(this.searchConfig.minSearchLength) || 1
    const isSearching = normalizedQuery.length >= minSearchLength
    const results = this.getSearchResults(query)
    const isEmpty = isSearching && results.length === 0
    const fragment = document.createDocumentFragment()
    const existingResults = new Map(
      [...this.searchResults.querySelectorAll('[data-filters-search-result]')].map(result => [
        `${result.dataset.searchGroup}\u0000${result.dataset.searchShortName}`,
        result
      ])
    )

    results.forEach(item => {
      const resultKey = `${item.groupName}\u0000${item.shortName}`
      fragment.append(this.createSearchResult(item, isSearching ? query : '', existingResults.get(resultKey)))
    })
    this.searchResults.replaceChildren(fragment)
    this.searchStatusLabel.textContent = isSearching ? this.searchConfig.resultsLabel : this.searchConfig.popularLabel
    this.searchStatusLabel.hidden = isEmpty
    this.searchCount.textContent = isSearching ? `${results.length} ${results.length === 1 ? 'Modell' : 'Modelle'}` : ''
    this.searchCount.hidden = !isSearching
    this.searchStatus.hidden = false
    this.searchResults.hidden = isEmpty
    this.searchEmpty.hidden = !isEmpty
    this.searchInput.setAttribute('aria-expanded', 'true')
  }

  createSearchResult(item, query, existingResult) {
    const button = existingResult || this.createResultButton()
    if (!button) return document.createDocumentFragment()

    const title = button.querySelector('[data-filters-search-result-title]')
    if (title) {
      title.replaceChildren()
      this.appendHighlightedText(title, item.shortName, query)
    }

    if (existingResult) return button

    button.setAttribute('aria-label', `${item.shortName}, ${item.groupName}`)
    button.dataset.searchGroup = item.groupName
    button.dataset.searchShortName = item.shortName

    const image = button.querySelector('[data-filters-search-result-image] img, img')
    if (image) {
      image.src = item.imageUrl
      image.alt = item.shortName
    }

    const description = button.querySelector('[data-filters-search-result-desc]')
    if (description) description.textContent = item.groupName

    return button
  }

  createResultButton() {
    const templateItem = this.resultTemplate?.content?.firstElementChild
    if (!templateItem) return null

    return templateItem.cloneNode(true)
  }

  appendHighlightedText(element, text, query) {
    const normalizedQuery = this.normalizeSearchValue(query)
    if (!normalizedQuery) {
      element.textContent = text
      return
    }

    const characterIndexes = []
    let normalizedText = ''
    ;[...text].forEach((character, index) => {
      if (!/[a-z0-9]/i.test(character)) return
      normalizedText += character.toLowerCase()
      characterIndexes.push(index)
    })

    const matchIndex = normalizedText.indexOf(normalizedQuery)
    if (matchIndex < 0) {
      element.textContent = text
      return
    }

    const startIndex = characterIndexes[matchIndex]
    const endIndex = characterIndexes[matchIndex + normalizedQuery.length - 1] + 1
    const highlight = document.createElement('mark')
    highlight.textContent = text.slice(startIndex, endIndex)
    element.append(text.slice(0, startIndex), highlight, text.slice(endIndex))
  }

  closeSearchResults() {
    if (!this.searchStatus || !this.searchResults || !this.searchInput) return

    if (this.searchRenderFrame) {
      window.cancelAnimationFrame(this.searchRenderFrame)
      this.searchRenderFrame = null
    }
    this.searchStatus.hidden = true
    this.searchResults.hidden = true
    this.searchEmpty.hidden = true
    this.searchInput.setAttribute('aria-expanded', 'false')
  }

  selectSearchResult(result) {
    this.flushSearchInputEvent()

    if (typeof window.WDSendEvent === 'function') {
      window.WDSendEvent('wd_search_select', {
        event_source: window.location.pathname,
        value: result.dataset.searchShortName
      })
    }

    this.dispatchEvent(
      new CustomEvent('filters-model-search:select', {
        bubbles: true,
        detail: {
          groupName: result.dataset.searchGroup,
          shortName: result.dataset.searchShortName
        }
      })
    )

    this.deactivateSearch()
  }

  handleSearchResultKeydown(event, result) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.closeSearchResults()
      this.searchInput.focus()
      return
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return

    const results = [...this.searchResults.querySelectorAll('[data-filters-search-result]')]
    const currentIndex = results.indexOf(result)
    let targetIndex = currentIndex

    if (event.key === 'ArrowDown') targetIndex = (currentIndex + 1) % results.length
    if (event.key === 'ArrowUp') targetIndex = (currentIndex - 1 + results.length) % results.length
    if (event.key === 'Home') targetIndex = 0
    if (event.key === 'End') targetIndex = results.length - 1
    if (targetIndex === currentIndex) return

    event.preventDefault()
    results[targetIndex]?.focus()
  }
}

if (!customElements.get('filters-model-search')) {
  customElements.define('filters-model-search', FiltersModelSearch)
}
